// Shared hours math for the employee portal (Home, Financial, Hours tab).
// "Counted" hours exclude rejected entries; callers may filter further by status.
// Structural input type: any entry-ish object with date/hours/status works.
export interface HoursEntry { work_date: string; hours: number; status: string }

export const ymdLocal = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

export const dayOf = (s?: string | null) => (s || '').slice(0, 10);

/** Mon–Sun window `weeksBack` weeks before the current one. */
export const weekWindow = (weeksBack: number) => {
  const d = new Date();
  const mon = new Date(d);
  mon.setDate(d.getDate() - ((d.getDay() + 6) % 7) - weeksBack * 7);
  const sun = new Date(mon);
  sun.setDate(mon.getDate() + 6);
  return { from: ymdLocal(mon), to: ymdLocal(sun) };
};

export const sumHours = (entries: HoursEntry[], from?: string, to?: string) =>
  entries
    .filter(e => {
      const d = dayOf(e.work_date);
      return (!from || d >= from) && (!to || d <= to);
    })
    .reduce((s, e) => s + Number(e.hours), 0);

export interface HoursStats {
  today: number;
  thisWeek: number;
  lastWeek: number;
  thisMonth: number;
  allTime: number;
  daysWorkedMonth: number;
  daysWorkedAllTime: number;
  avgPerWorkdayMonth: number;
  pendingHours: number;
  approvedHours: number;
}

export function computeHoursStats(entries: HoursEntry[]): HoursStats {
  const counted = entries.filter(e => e.status !== 'rejected');
  const today = ymdLocal(new Date());
  const monthStart = today.slice(0, 8) + '01';
  const w0 = weekWindow(0);
  const w1 = weekWindow(1);

  const monthEntries = counted.filter(e => dayOf(e.work_date) >= monthStart && dayOf(e.work_date) <= today);
  const daysWorkedMonth = new Set(monthEntries.map(e => dayOf(e.work_date))).size;
  const monthHours = monthEntries.reduce((s, e) => s + Number(e.hours), 0);

  return {
    today: sumHours(counted, today, today),
    thisWeek: sumHours(counted, w0.from, w0.to),
    lastWeek: sumHours(counted, w1.from, w1.to),
    thisMonth: monthHours,
    allTime: sumHours(counted),
    daysWorkedMonth,
    daysWorkedAllTime: new Set(counted.map(e => dayOf(e.work_date))).size,
    avgPerWorkdayMonth: daysWorkedMonth > 0 ? monthHours / daysWorkedMonth : 0,
    pendingHours: entries.filter(e => e.status === 'submitted').reduce((s, e) => s + Number(e.hours), 0),
    approvedHours: entries.filter(e => e.status === 'approved').reduce((s, e) => s + Number(e.hours), 0),
  };
}
